import { normalizeCheckItems } from "@/service/checkFlag";
import Vue from "vue";
import { ConfirmationBeforeEntry } from "@/model/ConfirmationBeforeEntry";
import { WorkShubetsu } from "@/model/WorkShubetsu";
import { InspectGensha } from "@/model/InspectGensha";
import { InspectDetail } from "@/model/InspectDetail";
import { CarSelectData } from "@/model/CarSelection";
import { InspectRecommend } from "@/model/InspectRecommend";
import { CustomerParams } from "@/model/CustomerParams";
import { NavigationItemParam } from "@/model/NavigationItemParam";
import { CustomerSave } from "@/model/CustomerSave";
import { InspectTime } from "@/model/InspectTime";
import store from "@/store";
import { JuuryouzeiJibaiseikiSave } from "@/model/JuuryouzeiJibaiseikiSave";
import { SpentTime } from "@/model/SpentTime";
import { AssignmentWork } from "@/model/AssignmentWork";
import { EditPriceParam } from '@/model/EditPriceParam';
import { ExtendsImageVO } from "@/model/ExtendsImage";
import { Buhin } from "@/model/Buhin";
import { ReservationSearchData } from "@/model/ReservationSearchData";
import { Auth } from "@/service/auth";
import { Utils } from "@/service/util";
import { SessionService } from "@/service/sessionService";
import {
  AssignReq,
  CarSelectionQuickMitsumoriCustomerReq,
  CompletedReq,
  ConfirmStateReq,
  CustomerSaveCouponSelfReq,
  CustomerSaveShakenHiyouSelfReq,
  DeleteManualSeibiReq,
  InspectDescriptionsReq,
  JizenReq,
  JizenUketsukeInspectionSeibiStatusReq,
  PartsSelectionInspectReq,
  QuickMitsumoriQrReq,
  RecommendQuickReq,
  RecommendReq,
  TechInfoAmountReq
} from "@/model/BackendRequestModel";

export default class extends Vue {
  baseAPI = process.env.VUE_APP_API_URL;
  // 長寿命のサービスインスタンスにも失効を即座に反映する。
  get token() { return store.getters["Authentication/token"]; }
  auth = new Auth();
  utils = new Utils();
  isLoggedIn = false;
  frontendName = "shakenFE";
  frontendVersion = process.env.VUE_APP_VERSION || "unknown";

  constructor() {
    super();
    this.isLoggedIn = this.auth.loggedIn();
  }

  /**
   * 車検整備種別は後方互換のため未設定時に通常整備（0）として扱う。
   * 他の必須パラメータは補完せず、呼び出し元の不備として扱う。
   */
  private normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn?: number | string): number | string {
    if (sknSeibiShubetsuKbn === undefined || sknSeibiShubetsuKbn === null || sknSeibiShubetsuKbn === "") {
      return 0;
    }
    return sknSeibiShubetsuKbn;
  }

  shouldSkipRequest(skipLoginSessionCheck: boolean = false) {
    return !skipLoginSessionCheck && !this.isLoggedIn;
  }

  // FEバージョンを各APIでBEに送信する
  private buildHeaders(headers: any = {}) {
    const uuid = this.utils.getShakenUuid();
    return {
      ...headers,
      "X-shaken-UUID": uuid,
      "X-Frontend-Name": this.frontendName,
      "X-Frontend-Version": this.frontendVersion
    };
  }

  private async waitForSessionCheck(skipLoginSessionCheck: boolean = false): Promise<boolean> {
    if (skipLoginSessionCheck) {
      return true;
    }
    // 業務APIは、期限延長が必要な場合だけPUT /auth/tokenの完了を待ってから送信する。
    const isSessionAvailable = await Auth.ensureSessionRenewed();
    this.isLoggedIn = this.auth.loggedIn();
    return isSessionAvailable && !SessionService.hasConnectionProblem() && !this.shouldSkipRequest();
  }

  async getReturnHeader(url: string) {
    if (!(await this.waitForSessionCheck())) {
      return { status: 0, skipped: true };
    }
    const promise1 = new Promise((resolve, reject) => {
      const headers = this.buildHeaders({ auth: this.token });
      this.$http.get(url, { headers }).then(
        (response: any) => {
          if (response.status == 200) {
            if (response.body) {
              const data = response.body;
              const header = response.headers.map;
              resolve({ ...data, header });
            } else {
              resolve(response.status);
            }
          } else {
            reject({ status: response.status });
          }
        },
        (response: any) => {
          let message = {};
          if (response.body) {
            message = response.body;
          }
          this.handleError(response);
          reject({ status: response.status, ...message });
        }
      );
    });
    return promise1;
  }

  async get(url: string, skipLoginSessionCheck: boolean = false, timeout?: number) {
    if (!(await this.waitForSessionCheck(skipLoginSessionCheck))) {
      // セッションログアウト後のAPI発行を抑制
      return Promise.resolve({ status: 0, skipped: true });
    }
    const requestStartedAt = Date.now();
    const promise1 = new Promise((resolve, reject) => {
      let headers: any = { auth: this.token };
      headers = this.buildHeaders(headers);
      const requestOptions: any = { headers };
      if (timeout) {
        requestOptions.timeout = timeout;
      }
      this.$http.get(url, requestOptions).then(
        (response: any) => {
          if (response.status == 200) {
            if (response.body) {
              resolve(response.json());
            } else {
              resolve(response.status);
            }
          } else {
            reject({ status: response.status });
          }
        },
        (response: any) => {
          let message = {};
          if (response.body) {
            message = response.body;
          }
          this.handleError(response);
          const isSessionRefreshTimeout = !!timeout && Date.now() - requestStartedAt >= timeout;
          reject({ status: response.status, isSessionRefreshTimeout, ...message });
        }
      );
    });
    return promise1;
  }
  async post(url: string, parameter: any, authHeader: boolean = true, skipLoginSessionCheck: boolean = false) {
    if (!(await this.waitForSessionCheck(skipLoginSessionCheck))) {
      // セッションログアウト後のAPI発行を抑制
      return Promise.resolve({ status: 0, skipped: true });
    }
    let headers: any = { "Content-Type": "application/json" };
    if (authHeader && this.token != null) {
      headers = { ...headers, auth: this.token };
    }
    headers = this.buildHeaders(headers);
    const promise1 = new Promise((resolve, reject) => {
      this.$http
        .post(url, parameter, {
          headers
        })
        .then(
          (response: any) => {
            if (response.body) {
              resolve({ ...response.body, headers: response.headers.map });
            } else {
              resolve({ status: response.status });
            }
          },
          (response: any) => {
            let message = {};
            if (response.body) {
              message = response.body;
            }
            if (authHeader) {
              this.handleError(response);
            }
            reject({ status: response.status, ...message });
          }
        );
    });
    return promise1;
  }

  async delete(url: string, data: any = null, skipLoginSessionCheck: boolean = false) {
    if (!(await this.waitForSessionCheck(skipLoginSessionCheck))) {
      return { status: 0, skipped: true };
    }
    const promise1 = new Promise((resolve, reject) => {
      let req: any;
      const headers = this.buildHeaders({ auth: this.token });
      if (data) {
        req = this.$http.delete(url, { headers, body: data });
      } else {
        req = this.$http.delete(url, { headers });
      }
      req.then(
        (response: any) => {
          resolve(response.status);
        },
        (response: any) => {
          this.handleError(response);
          reject(response.status);
        }
      );
    });
    return promise1;
  }

  async put(url: string, parameter?: any, skipLoginSessionCheck: boolean = false, timeout?: number) {
    if (!(await this.waitForSessionCheck(skipLoginSessionCheck))) {
      // セッションログアウト後のAPI発行を抑制
      return Promise.resolve({ status: 0, skipped: true });
    }
    const requestStartedAt = Date.now();
    const requestOptions: any = {
      headers: this.buildHeaders({ auth: this.token, "Content-Type": "application/json" })
    };
    if (timeout) {
      requestOptions.timeout = timeout;
    }
    const promise1 = new Promise((resolve, reject) => {
      this.$http
        .put(url, parameter, requestOptions)
        .then(
          (response: any) => {
            resolve(response);
          },
          (error: any) => {
            let message = {};
            if (error.body) {
              message = error.body;
            }
            this.handleError(error);
            const isSessionRefreshTimeout = !!timeout && Date.now() - requestStartedAt >= timeout;
            reject({ status: error.status, isSessionRefreshTimeout, ...message });
          }
        );
    });
    return promise1;
  }

  async pdf(url: string) {
    if (!(await this.waitForSessionCheck())) {
      return { status: 0, skipped: true };
    }
    const promise1 = new Promise((resolve, reject) => {
      let headers: any = { "Content-Type": "application/pdf", auth: this.token };
      headers = this.buildHeaders(headers);
      const header: any = {
        headers: headers,
        responseType: "blob"
      };
      this.$http.get(url, header).then(
        (response: any) => {
          if (response.status == 200) {
            if (response.body) {
              const blob = new Blob([ response.data ]);
              resolve(blob);
            } else {
              resolve(response.status);
            }
          } else {
            reject({ status: response.status });
          }
        },
        (response: any) => {
          let message = {};
          if (response.body) {
            message = response.body;
          }
          this.handleError(response);
          reject({ status: response.status, ...message });
        }
      );
    });
    return promise1;
  }

  authentication(param: { id: string; pass: string ;assignTypeKbn: string }) {
    return this.post(`${this.baseAPI}/auth/token`, param, false, true);
  }

  refreshToken(timeout?: number) {
    return this.put(`${this.baseAPI}/auth/token`, undefined, true, timeout);
  }

  checkSession(timeout?: number) {
    return this.get(`${this.baseAPI}/auth/token`, true, timeout);
  }

  logout() {
    return this.delete(`${this.baseAPI}/auth/token`, null, true);
  }

  getKbnSkns(sknTitle: string, kbnCd?: string) {
    let path = `${this.baseAPI}/kbnskns/${sknTitle}`;
    if (kbnCd) {
      path += "/" + kbnCd;
    }
    return this.get(path);
  }

  getQuestionnaireList(uketsukeExtendsId: number) { // AK-0001
    let path = `${this.baseAPI}/customer/questionnaire-extreme/${uketsukeExtendsId}`;
    return this.get(path);
  }

  saveQuestionnaire(data: any) { // AK-0001
    return this.put(`${this.baseAPI}/customer/questionnaire-extreme`, data);
    // 伝播ソケット:ソケット仮21
  }

  // getKbnByKbnCd(kbnCd: string) {
  //  return this.get(`${this.baseAPI}/kbns?q=kbnCd=${kbnCd}`);
  // }

  getStaffs(kyotenCd: string) {
    return this.get(`${this.baseAPI}/staffs?q=kyotenCd=${kyotenCd}`);
  }

  getEnvironment(kyotenCd: string) {
    return this.get(
      `${this.baseAPI}/user/setting/environment?q=kyotenCd=${kyotenCd}`
    );
  }

  // common
  getJuuryouzeiKubuns() {
    return this.get(`${this.baseAPI}/common-api/tax/juuryouzei/kubuns`);
  }

  getJuuryouzeiTable(kbn: string, date: string) {
    return this.get(
      `${this.baseAPI}/common-api/tax/juuryouzei/taxTable?q=kazeiKbn=${kbn},targetDate=${date}`
    );
  }

  getJuuryouzeiDetail(code: string) {
    return this.get(`${this.baseAPI}/common-api/tax/juuryouzei/detail/${code}`);
  }

  getJibaisekiTable(add1month: number, targetdate: string) {
    return this.get(
      `${this.baseAPI}/common-api/tax/jibaiseki/paymentTable` +
      `?q=isNewCar=0,add1Month=${add1month},targetDate=${targetdate}`
    ).then((response: any) => {
      if (!response || !response.jibaiseki || !Array.isArray(response.jibaiseki.results)) {
        throw new Error("自賠責保険料のデータ形式が不正です。");
      }
      return response;
    });
  }

  /** 2023-10-23 disp uchizei (invoice part 8) [START] */
  async getZeikomi( priceZn: number ) {
    return this.get(`${this.baseAPI}/user/getZeikomi/${priceZn}`);
  }
  /** 2023-10-23 disp uchizei (invoice part 8) [END] */

  async getZeinuki( priceZn: number ) {
    return this.get(`${this.baseAPI}/user/getZeinuki/${priceZn}`);
  }

  getServerTime() {
    return this.get(`${this.baseAPI}/common-api/etc/serverTime`, true);
  }

  async getSoukouUnit() {
    return await this.get(`${this.baseAPI}/common-api/etc/kbn/soukouUnitKbn`);
  }

  async getSoukouBikou() {
    return await this.get(`${this.baseAPI}/common-api/etc/kbn/soukouBikouKbn`);
  }

  convertMiles_Km(value: number, soukouUnitKbn: string = '1') {
    const pathconvert =
      soukouUnitKbn === '1' ? 'convertKmToMiles' : 'convertMilesToKm';
    const path = `${this.baseAPI}/common-api/car/soukou/${pathconvert}?q=value=${value}`;

    return this.get(path);
  }

  async asyncGetServerTime() {
    try {
      let serverTime: any = await this.getServerTime();
      if (serverTime && typeof serverTime.milliEpoch === "number") {
        return Promise.resolve(serverTime.milliEpoch);
      }
      return Promise.resolve(new Date().getTime());
    } catch (ex) {
      return Promise.resolve(new Date().getTime());
    }
  }

  getKanaQ(kanaIndex: string) {
    if (kanaIndex) {
      return `,kanaIndex%3D${kanaIndex}`;
    }
    return "";
  }

  getKyotenList() {
    return this.get(`${this.baseAPI}/kyotens`);
  }

  // navigation
  getNavigation(uketsukeExtendId: number, isCustomer: boolean = false) {
    let pathCustomer = isCustomer ? "/customer" : "";
    return this.get(
      `${this.baseAPI}${pathCustomer}/navigations/${uketsukeExtendId}`
    );
  }

  // sub header
  getSubHeaderData(uketsukeExtendId: number, part: string) {
    return this.get(`${this.baseAPI}/${part}/subheaders/${uketsukeExtendId}`);
  }

  saveSpentTime(data: SpentTime) {
    return this.post(`${this.baseAPI}/spent-time`, data);
    // 伝播ソケット:ソケット仮10
  }

  // after press start md-0002
  postNavigation(uketsukeExtendId: number) {
    return this.post(`${this.baseAPI}/navigations/`, {
      uketsukeExtendId: uketsukeExtendId
    });
  }

  // send current status to api
  putNavigation(param: NavigationItemParam) {
    return this.put(`${this.baseAPI}/navigations/`, param);
    // 伝播ソケット:ソケット仮14
  }

  // md-0000
  getReservationList(from: string, to: string) {
    let q = `workPlansYmdFrom=${from},workPlansYmdTo=${to}`;
    return this.get(
      `${
      this.baseAPI
      }/reservations?q=${q}`
    );
  }

  getReservationList2(searchData: ReservationSearchData) {
    const {
      nyuukoPlansStart,
      nyuukoPlansEnd,
      plateArea = "",
      plateModel = "",
      plateKana = "",
      plateNo = "",
      workShubetsu = "",
      kkUketsukeNm = "",
      kkUketsukeNmKn = "",
      shadaiNo = "",
      staffKensain = [],
      staffSeibishi = [],
      staffHojoseibishi = [],
      shuruiSeibiMenu = [],
      cntSeibiImageType,
      cntCarImageType,
      yoyakuNo = "",
      kkTel = "",
      skNo = "",
      kkNo = ""
    } = searchData;

    // 複数選択を文字列形式に折りたたむ
    const staffKensainList = Array.isArray(staffKensain)
      ? staffKensain
      : (staffKensain ? [String(staffKensain)] : []);
    const staffSeibishiList = Array.isArray(staffSeibishi)
      ? staffSeibishi
      : (staffSeibishi ? [String(staffSeibishi)] : []);
    const staffHojoseibishiList = Array.isArray(staffHojoseibishi)
      ? staffHojoseibishi
      : (staffHojoseibishi ? [String(staffHojoseibishi)] : []);
    const shuruiSeibiMenuList = Array.isArray(shuruiSeibiMenu)
      ? shuruiSeibiMenu
      : (shuruiSeibiMenu ? [String(shuruiSeibiMenu)] : []);


    // 各値に対して同様の空チェック処理を行いながら変数(query)に情報を纏める
    const query: string[] = [];
    const append = (key: string, value: string | number | null | undefined) => {
      if (value === null || value === undefined || value === "") {
        return;
      }
      query.push(`${key}=${encodeURIComponent(String(value))}`);
    };

    if (nyuukoPlansStart && nyuukoPlansEnd) {
      append("work_plans_ymd_from", nyuukoPlansStart);
      append("work_plans_ymd_to", nyuukoPlansEnd);
    }

    // NOTE FOR CODEX:
    // plate_area_target must not be sent from frontend.
    // Backend derives target list from plate_area in ReservationServiceImpl#getReservationsSearch().

    append("plate_area", plateArea);
    append("plate_model", plateModel);
    append("plate_kana", plateKana);
    append("plate_no", plateNo);
    append("work_shubetsu", workShubetsu);
    append("kk_uketsuke_nm", kkUketsukeNm);
    append("kk_uketsuke_nm_kn", kkUketsukeNmKn);
    append("shadai_no", shadaiNo);
    append("staff_kensain", staffKensainList.length > 0 ? staffKensainList.join("|") : "");
    append("staff_seibishi", staffSeibishiList.length > 0 ? staffSeibishiList.join("|") : "");
    append("staff_hojoseibishi", staffHojoseibishiList.length > 0 ? staffHojoseibishiList.join("|") : "");
    append("shurui_seibi_menu", shuruiSeibiMenuList.length > 0 ? shuruiSeibiMenuList.join("|") : "");
    append("cnt_seibi_image_type", cntSeibiImageType != 0 ? cntSeibiImageType : "");
    append("cnt_car_image_type", cntCarImageType != 0 ? cntCarImageType : "");
    append("yoyaku_no", yoyakuNo);
    append("kk_tel", kkTel);
    append("sk_no", skNo);
    append("kk_no", kkNo);

    const q = query.join(",");

    return this.get(
      `${
      this.baseAPI
      }/reservationsSearch?q=${q}`
    );
  }

  getWorkShubetsuCategories() {
    return this.get(`${this.baseAPI}/work-shubetsu-categories`);
  }

  getWorkShubetsu() {
    return this.get(`${this.baseAPI}/work-shubetsus`);
  }

  saveWorkShubetsu(works: WorkShubetsu[]) {
    return this.put(`${this.baseAPI}/work-shubetsu-managements`, works);
  }

  getReservationSetting() {
    return this.get(`${this.baseAPI}/reservations/column`);
  }

  updateReservationSetting(data: any[]) {
    return this.put(`${this.baseAPI}/reservations/column`, data);
  }

  // md-0001, ak-0001

  getLaborRate() {
    return this.get(`${this.baseAPI}/seibi-labor-rate`);
  }

  async getReservationDetail(code: string, isCustomer: boolean) {
    let path;
    if (isCustomer) {
      path = `${this.baseAPI}/customer/reservations/${code}`;
    } else {
      path = `${this.baseAPI}/reservations/${code}`;
    }
    return await this.getReturnHeader(path);
  }

  saveMileage(data: CustomerParams) {
    return this.put(`${this.baseAPI}/reservations/soukoukm`, data);
    // 伝播ソケット:ソケット仮23
  }

  // 今後使う可能性あり
  // saveMemo(data: CustomerParams) {// 呼び出し元：なし
  //  return this.put(`${this.baseAPI}/reservations/sknmemo`, data);
  //  // 伝播ソケット:ソケット仮24
  // }

  saveFrontTantousha(data: CustomerParams) {
    return this.put(`${this.baseAPI}/reservations/front-tantousha`, data);
    // 伝播ソケット:ソケット仮25
  }

  saveOmonaYouto(data: CustomerParams) {
    return this.put(`${this.baseAPI}/customer/reservations/omona-youto`, data);
    // 伝播ソケット:ソケット仮20
  }

  // [2024/05/26]del : uketsukeExtendsId基準のgetSupport2()に切り替え
  // // md-0001-1, ak-0001-1
  // getSupport(kokyakuKanriNo: string) {
  //   return this.get(
  //     `${
  //     this.baseAPI
  //     }/reservations/histories?q=kokyakuKanriNo=${kokyakuKanriNo}`
  //   );
  // }

  /** サポート履歴情報を得る : uketsukeExtendsId基準 */
  getSupport2(uketsukeExtendsId: number) {
    return this.get(
      `${
      this.baseAPI
      }/reservations/${uketsukeExtendsId}/supportHistories2`
    );
  }

  // md-0001-1
  async saveTax(data: JuuryouzeiJibaiseikiSave) {
    return await this.post(`${this.baseAPI}/reservations/meisai-tax`, data);
    // 法定費用→伝播せず
  }
  saveRateKingakuZn(param: any) {
    return this.put(`${this.baseAPI}/reservations/labor`, param);
  }

  // ak-0001-1-2
  saveConfirmation(param: ConfirmationBeforeEntry) {
    return this.put(
      `${this.baseAPI}/customer/reservations/confirmation-before-entries`,
      param
    );
    // 伝播ソケット:ソケット仮19
  }

  // md-0002
  getLiftUse() {
    return this.get(`${this.baseAPI}/lift`);
  }

  sendNexInspection(uketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/send-nex-inspection/${uketsukeExtendsId}`
    // ,  inspectionDetail
      );
  }

  saveInsectionDetail(inspectionDetail: InspectDetail) {
    return this.put(
      `${this.baseAPI}/inspection-detail-selection`,
      inspectionDetail
    );
    // 伝播ソケット:ソケット仮26
  }

  getInsectionDetail(uketsukeExtendsId: number, sknSeibiShubetsuKbn?: number) {
    const normalizedSknSeibiShubetsuKbn = this.normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn);
    return this.get(
      `${this.baseAPI}/inspection-detail-selection/${uketsukeExtendsId}/${normalizedSknSeibiShubetsuKbn}`
    );
  }

  getReferReservation(uketsukeExtendsId: number, nwPlansYmd: any, shadaiNo: any) {
    return this.get(
      `${this.baseAPI}/refer-reservation-list/${uketsukeExtendsId}/${nwPlansYmd}/${shadaiNo}`
    );
  }

  getStaffKensain() {
    return this.get(`${this.baseAPI}/sknstaffs/kensain`);
  }

  getStaffSeibishi() {
    return this.get(`${this.baseAPI}/sknstaffs/seibishi`);
  }

  getStaffHojoseibishi() {
    return this.get(`${this.baseAPI}/sknstaffs/hojoseibishi`);
  }

  getStaffFront() {
    return this.get(`${this.baseAPI}/sknstaffs/front`);
  }


  getSeibiMenuList() {
    return this.get(`${this.baseAPI}/shurui-seibi-menu`);
  }

  saveSeibiMenu(data: any) {
    return this.put(`${this.baseAPI}/shurui-seibi-menu`, data);
  }

  // md-0003
  getCarSelection(uketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/car-selection/${uketsukeExtendsId}`);
  }

  saveCarSelection(data: CarSelectData) {
    return this.put(`${this.baseAPI}/car-selection`, data);
  }

  revertTireSize(data: CarSelectData) {
    return this.put(`${this.baseAPI}/revert-tiresize`, data);
  }

  /*旧日整連純正部品
   * getKakyuki(makerCd: string, modelCd: string) {
   *   return this.get(
   *     `${this.baseAPI}/kakyuki-shashu?q=makerCd=${makerCd},modelCd=${modelCd}`
   *   );
   * }
  */

  // getShashu(makerCd: string, modelCd: string, carData: CarSelectData) {
  //  let q = `makerCd=${makerCd},modelCd=${modelCd}`;
  //  if (carData.katashiki) {
  //    q += `,katashiki=${carData.katashiki}`;
  //  }
  //  if (carData.engineKatashiki) {
  //    q += `,engineKatashiki=${carData.engineKatashiki}`;
  //  }
  //  if (carData.kakyukiKbn) {
  //    q += `,kakyukiKbn=${carData.kakyukiKbn}`;
  //  }
  //  return this.get(`${this.baseAPI}/shashu?q=${q}`);
  // }

  // md-0005
  getInspectLineResult(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/inspect-line/${sknUketsukeExtendsId}`);
  }

  getInspectLineSeibi(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/inspect-line-seibi/${sknUketsukeExtendsId}`);
  }

  saveInspectLineResult(data: any) {
    return this.post(`${this.baseAPI}/inspect-line/`, data);
  }

  saveInspectLineSeibi(data: any) {
    return this.put(`${this.baseAPI}/inspect-line-seibi`, data);
    // 伝播ソケット:ソケット仮04
    // 伝播ソケット:ソケット仮06
  }

  // md-0004, md-0006
  getInspect(sknUketsukeExtendsId: number, pathName: string, sknSeibiShubetsuKbn?: string) {
    if (pathName !== "jizen") {
      return this.get(`${this.baseAPI}/${pathName}/${sknUketsukeExtendsId}`);
    }
    return this.get(
      `${this.baseAPI}/${pathName}/${sknUketsukeExtendsId}/${this.normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn)}`
    );
  }

  /*
   * WHY: inspectionSelections の routeService が gensha 用 response 型を直接受け取れるよう、
   * pathName 分岐ではなく gensha endpoint を明示する。
   */
  getGenshaInspect(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/gensha/${sknUketsukeExtendsId}`);
  }

  /*
   * WHY: legal56 用 service から endpoint を明示呼び出しし、page 側の pathName 分岐をなくす。
   */
  getLegal56Inspect(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/legal56/${sknUketsukeExtendsId}`);
  }

  /*
   * WHY: jizen は整備種別区分が endpoint の一部になるため、
   * jizen service だけが optional segment の有無を判断する。
   */
  getJizenInspect(sknUketsukeExtendsId: number, sknSeibiShubetsuKbn: string) {
    if (sknSeibiShubetsuKbn) {
      return this.get(`${this.baseAPI}/jizen/${sknUketsukeExtendsId}/${sknSeibiShubetsuKbn}`);
    }
    return this.get(`${this.baseAPI}/jizen/${sknUketsukeExtendsId}`);
  }

  /** 2021-11-08 sh #7530 improve speed.(No.1) [START] */
  getInspectByCategoryId(sknUketsukeExtendsId: number, pathName: string
    , sknInspectCategoryId: number, sknSeibiShubetsuKbn?: string
  ) {
    if (pathName !== "jizen") {
      return this.get(`${this.baseAPI}/${pathName}ByCategoryId/`
        +`${sknUketsukeExtendsId}/${sknInspectCategoryId}`);
    }
    return this.get(`${this.baseAPI}/${pathName}ByCategoryId/`
      +`${sknUketsukeExtendsId}/${sknInspectCategoryId}/${this.normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn)}`);
  }

  /*
   * WHY: カテゴリ遅延読込も gensha endpoint を明示し、routeService の interface と対応させる。
   */
  getGenshaInspectByCategoryId(
    sknUketsukeExtendsId: number,
    sknInspectCategoryId: number
  ) {
    return this.get(`${this.baseAPI}/genshaByCategoryId/${sknUketsukeExtendsId}/${sknInspectCategoryId}`);
  }

  /*
   * WHY: legal56 のカテゴリ遅延読込 endpoint を明示し、共通関数内の pathName 分岐を避ける。
   */
  getLegal56InspectByCategoryId(
    sknUketsukeExtendsId: number,
    sknInspectCategoryId: number
  ) {
    return this.get(`${this.baseAPI}/legal56ByCategoryId/${sknUketsukeExtendsId}/${sknInspectCategoryId}`);
  }

  /*
   * WHY: jizen のカテゴリ遅延読込は整備種別区分の optional segment があるため、
   * jizen 用 request 関数内で endpoint を確定する。
   */
  getJizenInspectByCategoryId(
    sknUketsukeExtendsId: number,
    sknInspectCategoryId: number,
    sknSeibiShubetsuKbn: string
  ) {
    if (sknSeibiShubetsuKbn) {
      return this.get(
        `${this.baseAPI}/jizenByCategoryId/` +
        `${sknUketsukeExtendsId}/${sknInspectCategoryId}/${sknSeibiShubetsuKbn}`
      );
    }
    return this.get(
      `${this.baseAPI}/jizenByCategoryId/` +
      `${sknUketsukeExtendsId}/${sknInspectCategoryId}`
    );
  }
  /** 2021-11-08 sh #7530 improve speed.(No.1) [END] */

  saveInspect(data: any, pathName: string) {
    return this.put(`${this.baseAPI}/${pathName}`, data);
  }

  /*
   * WHY: 保存も routeService ごとに endpoint を固定し、page から pathName 指定を消す。
   */
  saveGenshaInspect(data: any) {
    return this.put(`${this.baseAPI}/gensha`, data);
  }

  /*
   * WHY: legal56 保存 endpoint を service interface と 1:1 にして、誤った pathName を渡せない形にする。
   */
  saveLegal56Inspect(data: any) {
    return this.put(`${this.baseAPI}/legal56`, data);
  }

  /*
   * WHY: jizen 保存 endpoint を明示し、jizen 固有 service の保存契約を固定する。
   */
  saveJizenInspect(data: any) {
    return this.put(`${this.baseAPI}/jizen`, data);
  }

  // deleteManualSeibi(manualSeibiId: number) {
  //   return this.delete(`${this.baseAPI}/manual-seibi/${manualSeibiId}`);
  // }

  deleteManualSeibi(data: { sknUketsukeManualSeibiId: number, uketsukeExtendsId: number }) {
    return this.delete(`${this.baseAPI}/manual-seibi`, data);
  }

  deleteManualBuhin(manualSeibiBuhinId: number) {
    return this.delete(`${this.baseAPI}/manual-seibi-buhin/${manualSeibiBuhinId}`);
  }

  sendSocket(sknUketsukeExtendsId: number, sknUketsukeInspectId: number) {
    return this.get(`${this.baseAPI}/inspect-socket/${sknUketsukeExtendsId}/${sknUketsukeInspectId}`);
    // 伝播ソケット:ソケット仮04
  }

  // md-0008
  getInspectRecommend(sknUketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/recommend-inspect-seibi/${sknUketsukeExtendsId}`
    );
  }

  saveInspectRecommend(data: RecommendReq) {
    return this.put(`${this.baseAPI}/recommend-inspect-seibi`, data);
    // 伝播ソケット:ソケット仮22
    // 伝播ソケット:ソケット仮06
  }

  saveInspectRecommendManual(data: RecommendReq) {
    return this.put(`${this.baseAPI}/recommend-inspect-seibi-manual`, data);
    // 伝播ソケット:ソケット仮22
    // 伝播ソケット:ソケット仮06
  }

  // md-0009, md-0010, md-0012 update workProgressState
  updateWorkProgressState(data: {
    sknUketsukeExtendsId: number;
    screenCd: string;
    currentWorkProgressState: number;
    uketsukeCd: string;
    yoyakuNo: string;
  }) {
    return this.put(`${this.baseAPI}/workprogress`, data);
    // 伝播ソケット:ソケット仮33
  }

  // md-0011
  getAssignmentWork(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/workorders/${sknUketsukeExtendsId}`);
  }

  saveAssignmentWork(data: AssignmentWork) {
    return this.post(`${this.baseAPI}/workorders/`, data);
  }

  getOtherStaffList(sknUketsukeExtendsId: number, workId: any, isManual: boolean) {
    let qWorkId;
    if (isManual) {
      qWorkId = `sknHiyouManualWorkId=${workId}`;
    } else {
      qWorkId = `sknHiyouInspectWorkId=${workId}`;
    }
    return this.get(`${this.baseAPI}/workorders/other-staff?q=sknUketsukeExtendsId=${sknUketsukeExtendsId},${qWorkId}`);
  }

  getStaffList(sknUketsukeExtendsId: number, workId: any, isManual: boolean) {
    let qWorkId;
    if (isManual) {
      qWorkId = `sknHiyouManualWorkId=${workId}`;
    } else {
      qWorkId = `sknHiyouInspectWorkId=${workId}`;
    }
    return this.get(`${this.baseAPI}/workorders/staff?q=sknUketsukeExtendsId=${sknUketsukeExtendsId},${qWorkId}`);
  }

  // md-0015 (作業・部品選択)
  getPartSelection(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/part-selection/${sknUketsukeExtendsId}`);
  }

  getPartSelectionBuhin(
    sknUketsukeExtendsId: number,
    katashiki: string,
    engineKatashiki: string
  ): Promise<Buhin[]> {
    return this.get(`${this.baseAPI}/part-selection-buhin-extreme?`
      +`q=sknUketsukeExtendsId=${sknUketsukeExtendsId},katashiki=${katashiki},engineKatashiki=${engineKatashiki}`
    ) as Promise<Buhin[]>;
  }

  savePartSelection(data: PartsSelectionInspectReq) {
    return this.put(`${this.baseAPI}/part-selection`, data);
    // 伝播ソケット:ソケット仮04
    // 伝播ソケット:ソケット仮22
    // 伝播ソケット:ソケット仮06
  }

  /* 2024-06-01 yd #8864 [START] */
  getKatashikiList(
    katashiki: string,
    engineKatashiki: string,
    katashikiShiteiNo: string,
    ruibetsuKubunNo: string
  ) {
    return this.get(`${this.baseAPI}/katashiki-search?`
      +`q=katashiki=${katashiki},engineKatashiki=${engineKatashiki},`
      +`katashikiShiteiNo=${katashikiShiteiNo},ruibetsuKubunNo=${ruibetsuKubunNo}`);
  }

  getCustomerCarKatashiki(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/katashiki/${sknUketsukeExtendsId}`);
  }

  getNisseirenWorkByKatashiki(
    modelCd: string, katashiki: string, engineKatashiki: string, kakyukiKbn: number
    , sknUketsukeExtendsId: number, sknInspectDescriptionId: number, sknSeibiShubetsuKbn?: number
  ) {
    return this.get(
      `${this.baseAPI}/nisseiren-work-katashiki?`
      +`q=model_cd=${modelCd},katashiki=${katashiki},engineKatashiki=${engineKatashiki},kakyukiKbn=${kakyukiKbn}`
      +`,sknUketsukeExtendsId=${sknUketsukeExtendsId},sknInspectDescriptionId=${sknInspectDescriptionId}`
      +`,sknSeibiShubetsuKbn=${this.normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn)}`
    );
  }

  getNisseirenWorkBySeibiTitle(
    modelCd: string, katashiki: string, engineKatashiki: string, kakyukiKbn: number, sknUketsukeExtendsId: number
    , schSeibiTitle: string, sknSeibiShubetsuKbn?: number
  ) {
    return this.get(
      `${this.baseAPI}/nisseiren-work-seibi-title?`
      +`q=model_cd=${modelCd},katashiki=${katashiki},engineKatashiki=${engineKatashiki},kakyukiKbn=${kakyukiKbn}`
      +`,sknUketsukeExtendsId=${sknUketsukeExtendsId},schSeibiTitle=${schSeibiTitle}`
      +`,sknSeibiShubetsuKbn=${this.normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn)}`
    );
  }

  getJunseiBuhinByKatashiki(
    katashikiShiteiNo: string, ruibetsuKubunNo: string, katashiki: string,
    engineKatashiki: string, syaryouShiki: string, syaryouShuki: string,
    sknInspectDescriptionId: number) {
    return this.get(
      `${this.baseAPI}/junsei-buhin-katashiki?`
      +`q=katashikiShiteiNo=${katashikiShiteiNo},ruibetsuKubunNo=${ruibetsuKubunNo}`
      +`,katashiki=${katashiki},engineKatashiki=${engineKatashiki}`
      +`,syaryouShiki=${syaryouShiki},syaryouShuki=${syaryouShuki}`
      +`,sknInspectDescriptionId=${sknInspectDescriptionId}`
    );
  }

  getJunseiBuhinByNisseirenWork(
    katashikiShiteiNo: string, ruibetsuKubunNo: string, katashiki: string,
    engineKatashiki: string, syaryouShiki: string, syaryouShuki: string,
    nisseirenCd: string) {
    return this.get(
      `${this.baseAPI}/junsei-buhin-nisseiren-work?`
      +`q=katashikiShiteiNo=${katashikiShiteiNo},ruibetsuKubunNo=${ruibetsuKubunNo}`
      +`,katashiki=${katashiki},engineKatashiki=${engineKatashiki}`
      +`,syaryouShiki=${syaryouShiki},syaryouShuki=${syaryouShuki}`
      +`,nisseirenCd=${nisseirenCd}`
    );
  }

  getJunseiBuhinByHinmei(
    katashikiShiteiNo: string, ruibetsuKubunNo: string, katashiki: string,
    engineKatashiki: string, syaryouShiki: string, syaryouShuki: string,
    schHinmei: string) {
    return this.get(
      `${this.baseAPI}/junsei-buhin-hinmei?`
      +`q=katashikiShiteiNo=${katashikiShiteiNo},ruibetsuKubunNo=${ruibetsuKubunNo}`
      +`,katashiki=${katashiki},engineKatashiki=${engineKatashiki}`
      +`,syaryouShiki=${syaryouShiki},syaryouShuki=${syaryouShuki}`
      +`,schHinmei=${schHinmei}`
    );
  }
  /* 2024-06-01 yd #8864 [END] */

  updateKatashiki(data: any) {
    return this.put(`${this.baseAPI}/katashiki-update`, data);
  }

  // md-0014
  saveCompleted(data: CompletedReq) {
    return this.put(`${this.baseAPI}/completed`, data);
    // 伝播ソケット:32車検中止
  }

  // ak-0002
  getInsectionCost(sknUketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/customer/inspection-cost/${sknUketsukeExtendsId}`
    );
  }

  getInsectionCostQM(sknUketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/customer/inspection-cost-qm/${sknUketsukeExtendsId}`
    );
  }

  saveShohiyou(data: CustomerSaveShakenHiyouSelfReq) {
    return this.post(`${this.baseAPI}/customer/shohiyou`, data);
    // 伝播ソケット:15車検諸費用の編集
  }

  getRyoukinRank() {
    return this.get(`${this.baseAPI}/customer/inspection-cost/ryoukin-rank`);
  }

  getTaxWeightBusinessHours() {
    return this.get(`${this.baseAPI}/customer/inspection-cost/tax-weight-business-hours`);
  }

  getTaxWeightFromWeb(sknUketsukeExtendsId: number, requestDateType: number, requestDate: string) {
    return this.get(
      `${this.baseAPI}/customer/inspection-cost/get-tax-weight-from-web/${sknUketsukeExtendsId}` +
      `?requestDateType=${requestDateType}&requestDate=${encodeURIComponent(requestDate)}`
    );
  }

  // ak-0003-1
  // 現車・57(ユーザー側)※動作確認重要。
  getInspectSeibi(uketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/customer/inspections-seibi/${uketsukeExtendsId}`
    );
  }

  // 整備対象リスト(ユーザー側)
  getInspectSeibiAK3_1(uketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/customer/inspections?q=sknUketsukeExtendsId=${uketsukeExtendsId}`
    );
  }

  // 整備対象詳細(ユーザー側)
  getInspectSeibiAK3_2(uketsukeExtendsId: number, id: number, seibi: number, mode: number) {
    let q;
    if (mode == 1) {
      q = `sknUketsukeExtendsId=${uketsukeExtendsId},sknUketsukeInspectionId=${id}`;
    } else if (mode == 2) {
      q = `sknUketsukeExtendsId=${uketsukeExtendsId},seibiMode=${seibi},sknUketsukeInspectionSeibiId=${id}`;
    } else if (mode == 3) {
      q = `sknUketsukeExtendsId=${uketsukeExtendsId},seibiMode=${seibi},sknUketsukeManualSeibiId=${id}`;
    }
    return this.get(
      `${this.baseAPI}/customer/inspections?q=${q}`
    );
  }

  updateDoTenken(uketsukeExtendsId: number, doTenken: number,
    sknUketsukeInspectId: any, sknUketsukeInspectSeibiId: any = null, sknUketsukeManualSeibiId: any = null) {
    let data: any = {
      sknUketsukeExtendsId: uketsukeExtendsId,
      doTenken: doTenken
    };
    if (sknUketsukeManualSeibiId) {
      data = { ...data, sknUketsukeManualSeibiId };
    } else if (sknUketsukeInspectSeibiId) {
      data = { ...data, sknUketsukeInspectSeibiId };
    }
    if (sknUketsukeInspectId) {
      data = { ...data, sknUketsukeInspectId };
    }
    return this.put(
      `${this.baseAPI}/customer/inspections/do-tenken`, data
    );
    // 伝播ソケット:02作業指示の編集（するしない後日および部品代）
    // 伝播ソケット:13するしない後日編集
  }

  saveBuhinAk3_2(data: any) {
    return this.put(`${this.baseAPI}/customer/inspections/edit-buhin-price`, data);
    // 伝播ソケット:02作業指示の編集（するしない後日および部品代）
    // 伝播ソケット:ソケット仮06
  }

  resetBuhinPriceAk3_2(data: any) {
    return this.put(`${this.baseAPI}/customer/inspections/reset-buhin-price`, data);
    // 伝播ソケット:ソケット仮06
  }

  // ak-004, ak-0005
  updatePrice(data: EditPriceParam) {
    return this.put(`${this.baseAPI}/customer/inspections/edit-price`, data);
    // 伝播ソケット:ソケット仮01
  }
  // ak-0006
  getCoupon(sknUketsukeExtendsId: number, sknSeibiShubetsuKbn?: number) {
    const normalizedSknSeibiShubetsuKbn = this.normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn);
    return this.get(
      `${this.baseAPI}/customer/coupon/${sknUketsukeExtendsId}/${normalizedSknSeibiShubetsuKbn}`
    );
  }

  getOption(sknUketsukeExtendsId: number, sknSeibiShubetsuKbn?: number) {
    const normalizedSknSeibiShubetsuKbn = this.normalizeSknSeibiShubetsuKbn(sknSeibiShubetsuKbn);
    return this.get(
      `${this.baseAPI}/customer/option/${sknUketsukeExtendsId}/${normalizedSknSeibiShubetsuKbn}`
    );
  }

  saveCoupon(data: CustomerSaveCouponSelfReq) {
    return this.post(`${this.baseAPI}/customer/coupon`, {
      ...data,
      coupons: normalizeCheckItems(data.coupons),
      proposals: normalizeCheckItems(data.proposals),
      tokutens: normalizeCheckItems(data.tokutens),
      options: normalizeCheckItems(data.options)
    });
    // 伝播ソケット:ソケット仮11
  }

  saveOption(data: CustomerSaveCouponSelfReq) {
    return this.post(`${this.baseAPI}/customer/option`, {
      ...data,
      coupons: normalizeCheckItems(data.coupons),
      proposals: normalizeCheckItems(data.proposals),
      tokutens: normalizeCheckItems(data.tokutens),
      options: normalizeCheckItems(data.options)
    });
    // 伝播ソケット:ソケット仮12
  }

  getCouponLimit() {
    const path =
    //  `${this.baseAPI}/coupon-limit?q=kyotenCd=${kyotenCd},groupCd=${groupCd},shatenCd=${shatenCd}`;
      `${this.baseAPI}/coupon-limit`;
      return this.get(path);
  }

  handleError(error: any) {
    if (error.status == 401) {
      console.log(error);
      SessionService.handleUnauthorized();
    }
  }

  // ak-0007
  savePayment(data: CustomerSave) {
    return this.post(`${this.baseAPI}/customer/payments`, data);
    // 伝播ソケット:ソケット仮34
  }

  getIsConfirm(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/customer/confirm/${sknUketsukeExtendsId}`);
  }

  saveIsConfirm(data: ConfirmStateReq) {
    return this.put(`${this.baseAPI}/customer/confirm`, data);
  }

  // save and stop
  clearLiftStaff(sknUketsukeExtendsId: number) {
    return this.put(`${this.baseAPI}/clearliftstaff/${sknUketsukeExtendsId}`);
  }

  async saveRyoukinRank(data: JuuryouzeiJibaiseikiSave) {
    return await this.post(`${this.baseAPI}/reservations/ryoukin-rank`, data);
  }

  saveManualAk5(data: InspectDescriptionsReq) {
    return this.put(`${this.baseAPI}/customer/inspections-manual`, data);
    // 伝播ソケット:ソケット仮06
  }

  deleteUketsukeManualSeibi(data: DeleteManualSeibiReq) {
    return this.put(`${this.baseAPI}/customer/inspections-manual-delete`, data);
    // 伝播ソケット:ソケット仮06
  }

  updateUketsukeManualSeibi(data: InspectDescriptionsReq) {
    return this.put(`${this.baseAPI}/customer/inspections-manual-seibinm`, data);
    // 伝播ソケット:ソケット仮06
  }

  saveCancel(data: CompletedReq) {
    return this.put(`${this.baseAPI}/cancel`, data);
    // 伝播ソケット:32車検中止
  }

  getTireSize(strTireSize: string) {
    return this.get(
      `${this.baseAPI}/get-tiresize?q=strTireSize=${strTireSize}`
    );
  }

  updateKensaHiyou(data: CustomerSaveShakenHiyouSelfReq) {
    return this.post(`${this.baseAPI}/customer/kensaHiyou`, data);
    // 伝播ソケット:17車検検査費用と車検基本費用の編集
  }

  resetKensaHiyou(sknUketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/customer/reset-kensaHiyou/${sknUketsukeExtendsId}`
      // 車検検査費用/車検基本費用リセット →伝播せず
      // 車検諸費用 リセット→伝播せず
    );
  }

  resetKensaHiyouQM(sknUketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/customer/reset-kensaHiyou-qm/${sknUketsukeExtendsId}`
    );
  }

  getPlateAreas() {
    return this.get(`${this.baseAPI}/common-api/car/plateAreas`);
  }

  saveCarSelectionQuickMitsumori(data: CarSelectData) {
    return this.post(`${this.baseAPI}/car-selection-quick-mitsumori`, data);
  }

  getQuickMitsumoriIdByPlateNo( param: any ) {
    let q = "";
    let query: string[] = [];
    if (param) {
      Object.keys(param).forEach((key: string) => {
        if (param[key]) {
            query.push(`${key}=${param[key]}`);
        }
      });
    }
    if (query.length) {
      q = `?${query.join("&")}`;
    }
    return this.get(
      `${this.baseAPI}/car-selection-quick-mitsumoriid-by-uuid${q}`
    );
  }

  async getPrefectures() {
    return await this.get(`${this.baseAPI}/common-api/etc/prefectures`);
  }

  async convertZipToAddress(zipcode: any, prefFlag: any = 0) {

    let zipcodeFirst = zipcode.slice(0 , 3);
    let zipcodeSecond = zipcode.slice(3, zipcode.length);

    const path =
      `${this.baseAPI}/common-api/etc/zipCode/convertZipToAddress?`
      + `q=zipcodeFirst=${zipcodeFirst},zipcodeSecond=${zipcodeSecond},prefFlag=${prefFlag}`;
    return await this.get(path);
  }

  saveCarSelectionQuickMitsumoriCustomer(data: CarSelectionQuickMitsumoriCustomerReq) {
    return this.post(`${this.baseAPI}/car-selection-quick-mitsumori-customer`, data);
  }

  getPlateAreasFrequent() {
    return this.get(`${this.baseAPI}/car-selection-quick-mitsumoriid-plate-area-frequent`);
  }

  getQuickMitsumoriList(param: any) {
    let q = "";
    let query: string[] = [];
    if (param) {
      Object.keys(param).forEach((key: string) => {
        if (param[key]) {
            query.push(`${key}=${param[key]}`);
        }
      });
    }
    if (query.length) {
      q = `?${query.join("&")}`;
    }
    return this.get(`${this.baseAPI}/car-selection-quick-mitsumori-data${q}`);
  }

  getQuickMitsumoriQrInfo(id: any) {
    return this.get(`${this.baseAPI}/quick/qr/${id}`);
  }

  saveQuickMitsumoriQrInfo(data: QuickMitsumoriQrReq) {
    return this.post(`${this.baseAPI}/quick/qr/img/`, data);
  }

  getCompanyInfo() {
    return this.get(`${this.baseAPI}/company/kyoten-detail`);
  }

  getCustomerCarList(param: any, page: any, size: any) {
    let q = "";
    let query: string[] = [];
    if (param) {
      Object.keys(param).forEach((key: string) => {
        if (param[key]) {
            query.push(`${key}=${param[key]}`);
        }
      });
    }
    if (query.length) {
      q = `&q=${query.join(",")}`;
    }
    return this.get(`${this.baseAPI}/exp-customer-cars?page=${page}&size=${size}&sort=created_at${q}`);
  }

  updateShakenShurui(inspectionDetail: InspectDetail, mode: number) {
    if ( mode == 1 ) {
      return this.put(
        `${this.baseAPI}/update-shaken-shurui-wasFreeDrop`,
        inspectionDetail
      );
      // 伝播ソケット:08車検コース変更による再描画指示
    } else {
      return this.put(
        `${this.baseAPI}/update-shaken-shurui`,
        inspectionDetail
      );
      // 伝播ソケット:08車検コース変更による再描画指示
    }

  }

  getQmMitsumoriPreview(id: any) {
    return this.get(`${this.baseAPI}/recommend-inspect-seibi-qm-preview-check/${id}`);
  }

  // getFunctionSetting() {
  //  return this.get(`${this.baseAPI}/func/setting`);
  // }

  /** 2021-05-27 sh #7123 if change price, change the flag. [START] */
  saveIsConfirmRecommend(data: ConfirmStateReq) {
    return this.put(`${this.baseAPI}/customer/confirm/recommend`, data);
  }
  saveIsConfirmInspect(data: ConfirmStateReq) {
    return this.put(`${this.baseAPI}/customer/confirm/inspect`, data);
  }
  /** 2021-05-27 sh #7123 if change price, change the flag. [END] */

  /** 2021-07-21 sh #6989 implement quick mitsumori search.(priority 2) [START] */
  getFunctionSettingPM() {
    return this.get(`${this.baseAPI}/user/functionsetting`);
  }
  getQuickMitsumoriByIdForCustomer(id: any) {
    return this.get(`${this.baseAPI}/car-selection-quick-mitsumori-data/customer/${id}`);
  }
  /** 2021-07-21 sh #6989 implement quick mitsumori search.(priority 2) [END] */
  saveJizenRecommend(data: JizenReq) {
    return this.put(`${this.baseAPI}/jizen-recommend`, data);
    // 伝播ソケット:ソケット仮27
  }

  getJizenResult(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/jizen-result/${sknUketsukeExtendsId}`);
  }

  getSknEstimate(sknUketsukeExtendsId: number) {
    return this.pdf(`${this.baseAPI}/jizen/exportPdf/${sknUketsukeExtendsId}`);
  }

  getJizenMitsumoriPreview(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/jizen-preview/${sknUketsukeExtendsId}`);
  }

  getJizenVal() {
    return this.get(`${this.baseAPI}/jizen-val`);
  }

  getJizenInspectionSeibi(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/jizen-inspection-seibi/${sknUketsukeExtendsId}`);
  }

  /** 2021-08-24 sh #7361 it does not update seibiList for quick mitsumori. [START] */
  saveInspectRecommendQm(data: RecommendQuickReq) {
    return this.put(`${this.baseAPI}/recommend-inspect-seibi-qm`, data);
    // 伝播ソケット:ソケット仮07
  }
  /** 2021-08-24 sh #7361 it does not update seibiList for quick mitsumori. [END] */
  updateTSknUketsukeExtends(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/jizen-update/${sknUketsukeExtendsId}`);
  }

  updateSknWorkProgressStateOnHold(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/jizen-on-hold/${sknUketsukeExtendsId}`);
  }
  /** 2021-09-28 sh #7438 implements tech info amount. [START] */
  updateUseTechInfoAmount(data: TechInfoAmountReq) {
    return this.put(`${this.baseAPI}/customer/inspection-cost/tech-info-amount`, data);
  }
  /** 2021-09-28 sh #7438 implements tech info amount. [END] */
  updateUketsukeExtendsNyuukoYmd(inspectionDetail: InspectDetail) {
    return this.put(
      `${this.baseAPI}/update-nyuuko-ymd`,
      inspectionDetail
    );
  }
  getNyuukoInfo(sknUketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/nyuuko-info/${sknUketsukeExtendsId}`
    );
  }
  getNavigationJoukyou(sknUketsukeExtendsId: number) {
    return this.get(
      `${this.baseAPI}/navigation-joukyou/${sknUketsukeExtendsId}`
    );
  }

  /** 2021-10-11 sh #7458 change layaout at quick mitsumori. [START] */
  printQuickMitsumori(id: any) {
    return this.pdf(`${this.baseAPI}/qm/exportPdf/${id}`);
  }
  /** 2021-10-11 sh #7458 change layaout at quick mitsumori. [END] */

  /** 2021-10-25 sh #7493 change display of spec. [START] */
  saveJizenDisplayOrder(id: any, data: JizenUketsukeInspectionSeibiStatusReq[]) {
    return this.put(`${this.baseAPI}/jizen-display-order/${id}`, data);
    // 伝播ソケット:29車検項目の表示順並び替え
  }
  /** 2021-10-25 sh #7493 change display of spec. [END] */

  /** 2021-12-01 sh #7587 change checking of seibiIds. [START] */
  getSelectingSeibiIds(id: any) {
    return this.get(`${this.baseAPI}/getSelectingSeibiDetailIds/${id}`);
  }
  /** 2021-12-01 sh #7587 change checking of seibiIds. [END] */

  // 2022-01-31 morisako kobac-free add [START]
  setFreeOfCharge(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/free-of-charge/${sknUketsukeExtendsId}`);
  }
  // 2022-01-31 morisako kobac-free add [END]
  getNewInformation() {
    return this.get(`${this.baseAPI}/new-information`);
  }
  getInformation() {
    return this.get(`${this.baseAPI}/list-information`);
  }
  // 2022-07-00 BQR1109 手入力作業の工賃変更対応 add [START]
  getManualKouchin(data: InspectDescriptionsReq) {
    return this.put(`${this.baseAPI}/manual-kouchin`, data);
  }
  // updatePriceForFactory(data: EditPriceParam) {// 使ってない
  //  return this.put(`${this.baseAPI}/customer/inspections/factory/edit-price`, data);
  //  // 伝播ソケット:ソケット仮01
  // }
  saveJizenInspectRecommendManual(data: RecommendReq) {
    return this.put(`${this.baseAPI}/jizen-recommend-inspect-seibi-manual`, data);
    // 伝播ソケット:ソケット仮28
  }
  // 2022-07-00 BQR1109 手入力作業の工賃変更対応 add [END]
  // 2023-10-00 BQR1092(809) add 明細の並び順とextreme取込 [START]
  checkJizenSort(sknUketsukeExtendsId: number) {
    return this.get(`${this.baseAPI}/check-jizen-sort/${sknUketsukeExtendsId}`);
  }
  // 2023-10-00 BQR1092(809) add 明細の並び順とextreme取込 [END]
  copyReservationTarget(fromUketsukeCd: string,toUketsukeCd: string) {
    return this.get(`${this.baseAPI}/copy-reservations/${fromUketsukeCd}/${toUketsukeCd}`);
  }

  getReservationTarget(sknUketsukeExtendsId: number, detailFlg: number) {
    return this.get(`${this.baseAPI}/list-reservations/${sknUketsukeExtendsId}/${detailFlg}`);
  }

  changeReservationTarget(uketsukeCd: string) {
    return this.get(`${this.baseAPI}/select-reservations/${uketsukeCd}`);
  }

  deleteReservationTarget(uketsukeCd: string) {
    return this.get(`${this.baseAPI}/delete-reservations/${uketsukeCd}`);
  }

  openedReservationTarget(sknUketsukeExtendsId: number, data: AssignReq) {
    return this.put(`${this.baseAPI}/opened-reservation/${sknUketsukeExtendsId}`, data);
  }

  getExtendsImageInspect(sknUketsukeExtendsId: number, sknUketsukeInspectId: number | null = null) {
    return this.get(`${this.baseAPI}/extends-image-inspect/${sknUketsukeExtendsId}`
       + (sknUketsukeInspectId ? `/${sknUketsukeInspectId}` : ''));
  }
  async uploadExtendsImage(data: any, sknUketsukeExtendsId: number) {
    const ret = await this.post(`${this.baseAPI}/extends-image/${sknUketsukeExtendsId}`, data);
    this.$delete(ret!, 'headers'); // 何故かPOSTの戻り値にheadersが含まれるので削除する
    return ret;
  }
  updateExtendsImage(data: ExtendsImageVO) {
    return this.put(`${this.baseAPI}/extends-image`, data);
  }
  deleteExtendsImage(id: number) {
    return this.delete(`${this.baseAPI}/extends-image/${id}`);
  }
  updateCompletedExtendsImageInspect(sknUketsukeExtendsId: number, sknUketsukeInspectId: number | null = null) {
    // 画像登録画面の画像追加、順番入替、削除は複数APIを並列実行するので、完了時に通知する
    return this.get(`${this.baseAPI}/extends-image-update-completed/${sknUketsukeExtendsId}`
       + (sknUketsukeInspectId ? `/${sknUketsukeInspectId}` : ''));
    // 伝播ソケット:35画像登録
  }
}
